<template>
    <div class="pay-view">
        <ScrollableView class="tip-container">
            <TipAndPayForm
                v-if="isTipAndPayFormVisible"
                v-model:tip="tip"
                :totalConsumption="totalPayableAmount"
                :disabled="isProcessingPayment"
            />

            <div v-if="isTipSliderVisible" class="tip">
                <TipSlider v-model:tipPercent="tipPercent"></TipSlider>
            </div>
        </ScrollableView>
        <div class="signin" v-if="isSigninSectionVisible">
            <button type="button" class="btn" @click="onSignupClick">{{ trans.pay.signup }}</button>
            <div class="hint">{{ trans.pay.signupBenefits }}</div>
        </div>
        <div class="total-container">
            <TipSummaryTable :tip="tip" :discountAmount="discountAmount" :totalConsumption="totalPayableAmount" :showTip="useTip"></TipSummaryTable>
        </div>

        <div class="bottom" ref="bottomContainerElement" :class="{ moveaside: !isPaymentMethodSelectorOpened }">
            <div class="disclaimer" v-html="trans.pay.disclaimer"></div>
            <div class="btncontainer" ref="buttonContainerElement">
                <!-- pay button-->
            </div>
            <!-- payment method selector -->
        </div>
    </div>
    <Teleport to=".view.pay > .head > .title">
        <template v-if="isPayLater">{{ trans.pay.order }}</template>
        <template v-else>{{ trans.pay.title }}</template>
    </Teleport>
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { PaymentState } from "@/app/context/PayContext";
    import { PayController_new } from "@/app/controller/PayController_new";
    import { PayButton } from "@/app/elements/PayButton";
    import { PaymentMethodSelector } from "@/app/elements/PaymentMethodSelector";
    import { trans } from "@/app/localization/Trans";
    import { UserCreditCardsService } from "@/app/services/userCreditCardsService";
    import { Helper } from "@/app/ui/Helper";
    import { Popup } from "@/app/ui/Popup";
    import type { IPaymentIntent } from "@/data/model/Customer";
    import type { IOrderUseCase } from "@/data/model/OrderUseCase";
    import type { IUseCaseBase } from "@/data/model/UseCaseBase";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { toMoneyWithSign } from "@/shared/utils/format";
    import { computed, onMounted, ref, watch } from "vue";

    import TipSlider from "@/app/components/TipSlider.vue";
    import ScrollableView from "@/components/layout/ScrollableView.vue";
    import TipAndPayForm from "@/components/pay/TipAndPayForm.vue";
    import TipSummaryTable from "@/components/pay/TipSummaryTable.vue";

    function onSignupClick() {
        PayController_new.onShowSignup.dispatch();
    }

    // state
    // html elements
    const buttonContainerElement = ref<HTMLElement | null>(null);
    const bottomContainerElement = ref<HTMLElement | null>(null);

    // components
    const methodSelector = ref<PaymentMethodSelector | null>(null);
    const payBtn = ref<PayButton | null>(null);

    // state values
    const isPayLater = ref(false);
    const isTipAllowedForSelectedPaymentMethod = ref(true);
    const isWaiter = ref(false);
    const areEventsInitialized = ref(false);

    const tip = ref(null as number | null);
    const tipPercent = ref(undefined as number | undefined);
    const totalPayableAmount = ref(0);
    const discountAmount = ref(0);
    const isProcessingPayment = ref(false);
    const isAuthenticated = ref(false);

    const serviceUseCase = ref(undefined as IOrderUseCase | undefined);
    const useCase = ref(undefined as IUseCaseBase | undefined);
    const isSigninSectionVisible = computed(() =>
        isAuthenticated.value || (serviceUseCase.value !== undefined && !serviceUseCase.value.enableSignUpBeforePay)
            ? false
            : true
    );
    const useTip = computed(() => {
        return useCase.value === undefined || !isTipAllowedForSelectedPaymentMethod.value
            ? false
            : useCase.value.useTip;
    });

    const isViewVisible = ref(false);
    const isTipAndPayFormVisible = computed(() => isViewVisible.value && useTip.value && isWaiter.value);
    const isTipSliderVisible = computed(() => useTip.value && !isWaiter.value);

    const isPaymentMethodSelectorOpened = ref(false);

    function updatePayViewState() {
        // identity state
        isAuthenticated.value = TastierClient.identityContext.isAuthenticated();
        isWaiter.value = TastierClient.identityContext?.isWaiterInCurrentLocation();

        // pay context state
        isPayLater.value = TastierClient.payContext.isPayLater();
        isTipAllowedForSelectedPaymentMethod.value = TastierClient.payContext.isTipAllowedForSelectedPaymentMethod();

        useCase.value = Helper.getCurrentUseCase();
        serviceUseCase.value = Helper.getCurrentOrderServiceUseCase();

        // tip state
        totalPayableAmount.value = TastierClient.payContext.consumption;
        discountAmount.value = TastierClient.payContext.discountAmount;
        tip.value = TastierClient.payContext.tipAmount;
        tipPercent.value = TastierClient.payContext.tipPercent;

        payBtn.value!.setAmount(toMoneyWithSign(TastierClient.payContext.total, true));

        updatePaymentButtonAndMethodSelector();
    }

    PayController_new.onShown.add(onBecomeVisible, this);
    PayController_new.onHidden.add(onBecomeInvisible, this);

    onMounted(() => {
        // init payment method selector
        const $bottomContainer = $(bottomContainerElement.value! as any);
        methodSelector.value = new PaymentMethodSelector().appendTo($bottomContainer);
        methodSelector.value.onCardFormDestroyed = () => {
            TastierClient.payContext.clearPaymentMethod();
        };

        methodSelector.value.onSelect = (method, creditCardToken) => {
            if (method === undefined) {
                TastierClient.payContext.clearPaymentMethod();
            } else {
                return TastierClient.payContext.setPaymentMethod(method, creditCardToken);
            }
        };

        methodSelector.value.onModeChange = () => updatePaymentButtonAndMethodSelector();
        methodSelector.value.onOpenClose = () => updatePaymentButtonAndMethodSelector();

        // init pay button
        const $btnContainer = $(buttonContainerElement.value! as any);
        payBtn.value = new PayButton(translations.pay.payNow).appendTo($btnContainer);
        payBtn.value.onClick = async () => {
            const selectedItems = TastierClient.context.getSelectedItems();
            if (methodSelector.value!.isCardFormMode) {
                try {
                    await methodSelector.value!.validateCardForm();
                    TastierClient.payContext.pay(selectedItems, TastierClient.payContext.consumption, undefined, () => {
                        PayController_new.onGoBack.dispatch();
                    });
                } catch (ex) {
                    logError(ex);
                }
            } else {
                TastierClient.payContext.pay(selectedItems, TastierClient.payContext.consumption, undefined, () => {
                    PayController_new.onGoBack.dispatch();
                });
            }
        };

        payBtn.value.onDisabledClick = () => {
            if (TastierClient.payContext.state === PaymentState.MethodSelection) {
                methodSelector.value!.open();
            }
        };

        onBecomeVisible();
    });

    watch(tip, (newValue) => {
        if (isTipAndPayFormVisible.value) TastierClient.payContext.setTip(newValue ?? 0);
    });

    watch(tipPercent, (newValue) => {
        if (isTipSliderVisible.value) TastierClient.payContext.setTipInPercent(newValue ?? 0);
    });

    function onBecomeVisible(this: any) {
        isViewVisible.value = true;

        if (!areEventsInitialized.value) {
            TastierClient.payContext.onAmountChanged.add(updatePayViewState, this);
            TastierClient.payContext.onBillDiscountChanged.add(updatePayViewState, this);
            TastierClient.payContext.onStateChanged.add(updatePayViewState, this);
            TastierClient.payContext.onIntentChanged.add(intentUpdated, this);
            TastierClient.context.onCartChanged.add(onCartUpdated, this);
            TastierClient.identityContext.onIdentityChange.add(onAuthenticationChanged, this);
            UserCreditCardsService.onCreditCardsChanged.add(onCreditCardsChanged, this);
            areEventsInitialized.value = true;
        }

        updatePayViewState();
        resetPaymentMethods();
    }

    function onBecomeInvisible(this: any) {
        isViewVisible.value = false;

        if (areEventsInitialized.value) {
            TastierClient.payContext.onAmountChanged.remove(updatePayViewState, this);
            TastierClient.payContext.onStateChanged.remove(updatePayViewState, this);
          TastierClient.payContext.onBillDiscountChanged.remove(updatePayViewState, this);
            TastierClient.payContext.onIntentChanged.remove(intentUpdated, this);
            TastierClient.context.onCartChanged.remove(onCartUpdated, this);
            TastierClient.identityContext.onIdentityChange.remove(onAuthenticationChanged, this);
            UserCreditCardsService.onCreditCardsChanged.remove(onCreditCardsChanged, this);
            areEventsInitialized.value = false;
        }

        payBtn.value!.setAmount(toMoneyWithSign(0));
    }

    function resetPaymentMethods() {
        methodSelector.value!.setLoading();
        TastierClient.payContext.prepareForCheckout().catch((ex) => {
            logError(ex);
        });
    }

    function updatePaymentButtonAndMethodSelector() {
        methodSelector.value!.paymentInProgress = false;

        isPaymentMethodSelectorOpened.value = methodSelector.value!.isOpen;

        if (methodSelector.value!.isOpen) {
            if (!methodSelector.value!.isCardFormMode) {
                payBtn.value!.setDisabled();
            } else {
                if (TastierClient.payContext.state === PaymentState.Paying) {
                    methodSelector.value!.paymentInProgress = true;
                    payBtn.value!.setDisabled(trans.pay.paying);
                } else if (isPayLater.value) {
                    payBtn.value!.setEnabled();
                    payBtn.value!.setAction(trans.pay.order);
                } else {
                    payBtn.value!.setEnabled();
                    payBtn.value!.setAction(translations.pay.payNow);
                }
            }
        } else {
            switch (TastierClient.payContext.state) {
                case PaymentState.Preparing:
                    payBtn.value!.setDisabled();
                    payBtn.value!.setAction(translations.pay.payNow);
                    break;
                case PaymentState.MethodSelection:
                    payBtn.value!.setDisabled();
                    payBtn.value!.setAction(translations.pay.payNow);
                    break;
                case PaymentState.ReadyToPay:
                    if (isPayLater.value) {
                        payBtn.value!.setEnabled();
                        payBtn.value!.setAction(trans.pay.order);
                    } else {
                        payBtn.value!.setEnabled();
                        payBtn.value!.setAction(translations.pay.payNow);
                    }
                    break;
                case PaymentState.Paying:
                    methodSelector.value!.paymentInProgress = true;
                    payBtn.value!.setDisabled(trans.pay.paying);
                    break;
                case PaymentState.Confirmed:
                    payBtn.value!.setDisabled(trans.pay.btnSuccessful);
                    break;
                case PaymentState.Failed:
                    payBtn.value!.setDisabled(trans.pay.btnFailed);
                    break;
                case PaymentState.Ordering:
                    methodSelector.value!.paymentInProgress = true;
                    payBtn.value!.setDisabled(trans.pay.ordering);
                    break;
            }
        }
    }

    function intentUpdated(intent: IPaymentIntent) {
        methodSelector.value!.setAvailableOptions(intent.paymentMethods);
    }

    function onAuthenticationChanged() {
        if (TastierClient.payContext.state === PaymentState.Paying) {
            return;
        }

        updatePayViewState();
        resetPaymentMethods();
    }

    function onCreditCardsChanged() {
        if (TastierClient.payContext.state === PaymentState.Paying) {
            return;
        }

        resetPaymentMethods();
    }

    function onCartUpdated() {
        //dont notify if cart is already checked out / payment going on
        if (
            TastierClient.payContext.state === PaymentState.Paying ||
            TastierClient.payContext.state === PaymentState.Confirmed ||
            TastierClient.payContext.state === PaymentState.Failed
        )
            return;

        Popup.show(
            `<b>${translations.pay.cartChangedTitle}</b>
            <br /><br />
            ${translations.pay.cartChangedMessage}`,
            () => {
                PayController_new.onGoBack.dispatch();
            },
            undefined,
            undefined,
            "totalChanged"
        );
    }

    watch(isTipAndPayFormVisible, (shouldShowTipAndPayForm) => {
        // if value was "false" before - init tip and pay form
        if (shouldShowTipAndPayForm) {
            tip.value = null;
            tipPercent.value = undefined;
            totalPayableAmount.value = TastierClient.payContext.consumption;
            discountAmount.value = TastierClient.payContext.discountAmount;
            TastierClient.payContext.setTip(0);
        }
    });

    watch(useTip, (shouldUseTip) => {
        if (!shouldUseTip) {
            TastierClient.payContext.setTip(0);
            TastierClient.payContext.setTipInPercent(0);
            tip.value = 0;
            tipPercent.value = 0;
        }
        // if should use tip - dedicated watchers will handle it
    });
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .pay-view-container {
        position: absolute;
        top: 53px;
        left: 0;
        right: 0;
        bottom: 0;
    }

    .pay-view {
        display: flex;
        flex-direction: column;
        position: absolute;

        top: 0;
        left: 0;
        right: 0;
        bottom: 0;

        > .scrollable-view {
            flex: 1 1 auto;

            > .scrollable-content {
                padding-top: 2em;

                > .tip-and-pay-form-container {
                    padding: 0 25px;
                }

                > .tip {
                    padding: 14px 44px;

                    > .row {
                        padding: 8px 0;

                        &:after {
                            content: "";
                            clear: both;
                            display: table;
                        }

                        > .label {
                            width: 106px;
                            display: inline-block;
                            vertical-align: top;
                            font-size: 14px;
                            font-weight: 700;
                            line-height: 36px;
                            overflow: hidden;
                            white-space: nowrap;
                            text-overflow: ellipsis;
                            color: colors.$fontColor;
                        }

                        > .slider {
                            margin-top: -6px;
                            display: block;
                        }
                    }
                }

                > .paymentmethod {
                    padding: 4px 0;
                    text-align: center;
                }
            }
        }

        > .signin {
            flex: 0 0 auto;
            text-align: center;
            padding: 22px 26px;

            > .hint {
                font-size: 12px;
                padding: 16px 0 0 0;
                text-align: center;
            }

            > .btn {
                border: none;
                color: #fff;
                text-align: center;
                height: 46px;
                font-size: 16px;
                font-weight: 300;
                border-radius: 23px;
                background-color: var(--colorPrimary);
                color: #fff;
                display: block;
                width: 100%;

                @include mixins.hover-and-active() {
                    background-color: var(--colorPrimaryHover);
                }
            }
        }

        > .total-container {
            flex: 0 0 auto;
            padding: 0 24px;
        }

        > .bottom {
            flex: 0 0 auto;
            position: relative;
            height: 104px;
            background-color: #fff;
            -webkit-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);
            -moz-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);
            box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);
            z-index: 400;
            pointer-events: none;
            transition: padding 0.6s variables.$easeOutExpo;

            &.moveaside {
                > .disclaimer {
                    z-index: 200 !important;
                }

                > .btncontainer {
                    left: 85px;
                }

                > .paymentmethodselector > .openbtn {
                    opacity: 1;
                    transform: translate3d(0, 0, 0);
                }
            }

            > .disclaimer {
                padding: 0 12px 0 12px;
                font-size: 12px;
                text-align: center;
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
                color: colors.$fontColor;
                font-weight: 300;
                height: 38px;
                line-height: 38px;
                position: relative;
                z-index: 300;

                > a {
                    text-decoration: underline;
                    color: colors.$fontColor;
                    pointer-events: auto;

                    @include mixins.hover-and-active() {
                        color: colors.$fontColorHover;
                    }
                }
            }

            > .paymentmethodselector {
                position: absolute;
                left: 0;
                top: 0;
                right: 0;
                pointer-events: auto;

                > .openbtn {
                    opacity: 0;
                    transform: translate3d(-84px, 0, 0);
                    transition:
                        transform 0.6s variables.$easeOutExpo,
                        opacity 0.6s variables.$easeOutExpo;
                }
            }

            > .btncontainer {
                position: absolute;
                bottom: 10px;
                right: 12px;
                left: 12px;
                transition: left 0.6s variables.$easeOutExpo;

                > .paybtn {
                    z-index: 420;
                }
            }
        }
    }
</style>
